import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Bar } from '../components/ui';
import { elapsed, type DrillOutcome } from './meta';

/** 보기 고르기 문제 공통 (언어·자료·도형·공간·기계·색·문제 해결·상황 판단·지각 속도·집중). answer = 정답 보기 번호(0부터) */
export interface ChoiceQuestion {
  answer: number;
  /** 보기 개수 */
  n: number;
  limitSec: number;
  explain?: string;
  steps?: string[];
  /** 속도 과제: 고르면 설명 없이 바로 다음 문제 */
  fast?: boolean;
}

interface Answered { ok: boolean; given: number | null; ms: number; info: Record<string, unknown> }

const MARKS = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨'];

/** 문제마다 제한 시간 → 보기 고르기 → 맞으면 잠깐 보여 주고 넘어가고, 틀리면(풀이가 있으면 맞아도) '다음'을 누를 때까지 설명을 둔다.
 *  키보드: 숫자 1~9 = 보기, 보기가 둘이면 ←/→ 도 된다. */
export function ChoiceDrill<Q extends ChoiceQuestion>({ questions, seed, onDone, className, render, renderChoice, info, choicesClass = '', marks = true }: {
  questions: Q[];
  seed: number;
  onDone: (o: DrillOutcome) => void;
  className: string;
  /** 문제 화면. revealed = 답을 골랐음 */
  render: (q: Q, revealed: boolean) => ReactNode;
  renderChoice: (q: Q, i: number) => ReactNode;
  /** 틀린 문제 기록에 남길 내용 */
  info: (q: Q) => Record<string, unknown>;
  choicesClass?: string | ((q: Q) => string);
  /** 보기 앞 번호(①②…) 표시 */
  marks?: boolean;
}) {
  const started = useRef(performance.now());
  const asked = useRef(performance.now());
  const log = useRef<Answered[]>([]);
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<number | null | undefined>(undefined); // undefined = 아직, null = 시간 초과
  const [left, setLeft] = useState(1);
  const q = questions[index];
  const done = picked !== undefined;
  const ok = done && picked === q.answer;
  const waits = done && !q.fast && (!ok || !!q.steps);

  const next = () => {
    if (index + 1 >= questions.length) {
      const all = log.current;
      const times = all.map((a) => a.ms).sort((a, b) => a - b);
      onDone({
        correct: all.filter((a) => a.ok).length,
        total: all.length,
        duration_ms: elapsed(started.current),
        detail: { seed, median_ms: times[Math.floor(times.length / 2)], wrong: all.filter((a) => !a.ok).map((a) => ({ ...a.info, given: a.given })) },
      });
      return;
    }
    asked.current = performance.now();
    setPicked(undefined);
    setLeft(1);
    setIndex(index + 1);
  };

  const choose = (i: number | null) => {
    if (picked !== undefined || (i !== null && i >= q.n)) return;
    const hit = i === q.answer;
    log.current.push({ ok: hit, given: i, ms: elapsed(asked.current), info: info(q) });
    setPicked(i);
    if (q.fast) window.setTimeout(next, hit ? 150 : 700);
    else if (hit && !q.steps) window.setTimeout(next, 900);
  };
  const chooseRef = useRef(choose);
  chooseRef.current = choose;
  const twoRef = useRef(q.n === 2);
  twoRef.current = q.n === 2;

  useEffect(() => {
    if (done) return undefined;
    const id = window.setInterval(() => {
      const remain = 1 - (performance.now() - asked.current) / (q.limitSec * 1000);
      setLeft(Math.max(0, remain));
      if (remain <= 0) chooseRef.current(null);
    }, 100);
    return () => window.clearInterval(id);
  }, [index, done, q.limitSec]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.altKey || e.ctrlKey || e.metaKey) return;
      const d = Number(e.key);
      if (Number.isInteger(d) && d >= 1 && d <= 9) chooseRef.current(d - 1);
      else if (twoRef.current && e.key === 'ArrowLeft') chooseRef.current(0);
      else if (twoRef.current && e.key === 'ArrowRight') chooseRef.current(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const cls = typeof choicesClass === 'function' ? choicesClass(q) : choicesClass;
  return (
    <div className={`drill choice-drill ${className} ${done ? (ok ? 'ok' : 'miss') : ''}`}>
      <div className="row-between">
        <span className="drill-progress">{index + 1} / {questions.length}</span>
        <span className="small muted">{Math.ceil(left * q.limitSec)}초</span>
      </div>
      <Bar value={left} tone={left < 0.3 ? 'warn' : 'accent'} />
      {render(q, done)}
      <div className={`choice-list ${cls} ${marks ? 'marked' : ''}`}>
        {Array.from({ length: q.n }, (_, i) => (
          <button
            key={`${index}-${i}`} type="button" disabled={done} onClick={() => choose(i)}
            className={`choice ${done && i === q.answer ? 'right' : done && i === picked ? 'wrong' : ''}`}
          >
            {marks && <span className="choice-mark" aria-hidden="true">{MARKS[i]}</span>}
            {renderChoice(q, i)}
          </button>
        ))}
      </div>
      <div aria-live="polite">
        {done && q.fast && <p className="rule-feedback">{ok ? '✓' : picked === null ? '시간 초과' : '✗'}</p>}
        {done && !q.fast && (
          <>
            <p className="rule-feedback">{ok ? '정답!' : picked === null ? '시간 초과 — 초록색이 정답' : '틀렸습니다 — 초록색이 정답'}</p>
            {q.steps ? <ol className="biz-steps">{q.steps.map((s) => <li key={s}>{s}</li>)}</ol> : q.explain ? <p className="seq-explain">{q.explain}</p> : null}
            {waits && (
              <div className="drill-end">
                <button type="button" className="btn btn-primary" autoFocus onClick={next}>다음</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
